/**
 * MorningBriefPage — an automated every-morning scan of the tenant's key
 * numbers (certified measures + pinned KPIs) that produces a short proactive
 * briefing: a written summary plus Anomalies, Top Movers and Threshold
 * Breaches. Admins turn it on, set the send time / sensitivity / channels and
 * threshold rules; anyone with read access can preview on demand and browse
 * the history of past mornings. Scoped to the current tenant.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Sunrise, Loader2, Trash2, Sparkles, Copy, ChevronDown, ChevronRight,
  Check, CheckCircle2, RefreshCw, Settings2, Plus, AlertTriangle, TrendingUp, Bell, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { SlackChannelPicker, TeamsChannelPicker } from '@/components/ChannelPickers';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Hash } from 'lucide-react';

const STATUS_CLS = {
  running: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  completed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  failed: 'bg-destructive/15 text-destructive border-destructive/30',
};
const TONE_CLS = {
  attention: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  calm: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
};
const SECTION_ICON = { anomalies: AlertTriangle, movers: TrendingUp, breaches: Bell, summary: Sparkles };
const inputCls = 'w-full px-3 py-2 rounded-lg bg-muted/40 border border-border/50 text-sm text-foreground outline-none focus:border-primary/50';

function fmtDate(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleString(); } catch { return String(v).slice(0, 16); }
}

export default function MorningBriefPage() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [emailsText, setEmailsText] = useState('');
  const [slackChannels, setSlackChannels] = useState([]);
  const [teamsChannels, setTeamsChannels] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [measures, setMeasures] = useState([]);
  const [online, setOnline] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [loadingOnlineId, setLoadingOnlineId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [slackPreview, setSlackPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dmSending, setDmSending] = useState(false);
  const [dmStatus, setDmStatus] = useState(null);
  useEffect(() => { api.slackDmStatus().then(setDmStatus).catch(() => setDmStatus(null)); }, []);
  const sendSlackDm = async () => {
    setDmSending(true);
    try { await api.sendSlackDmPreview(); toast.success('Preview sent to your Slack DM'); }
    catch (e) { toast.error(e.message || 'Could not send Slack DM'); }
    finally { setDmSending(false); }
  };
  const openSlackPreview = async () => {
    setPreviewLoading(true);
    try { setSlackPreview(await api.slackDigestPreview()); }
    catch (e) { toast.error(e.message || 'Preview failed'); }
    finally { setPreviewLoading(false); }
  };
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [bRes, cRes, mRes] = await Promise.all([
        api.getMorningBriefs(),
        api.getMorningBriefConfig(),
        api.getMorningBriefMeasures().catch(() => ({ measures: [] })),
      ]);
      const items = bRes.items || [];
      setBriefs(items);
      setCfg(cRes);
      setEmailsText((cRes?.notify_emails || []).join(', '));
      setSlackChannels(cRes?.notify_slack_channels || []);
      setTeamsChannels(cRes?.notify_teams_channels || []);
      setMeasures(mRes?.measures || []);
      return items;
    } catch (e) {
      toast.error(e.message || 'Failed to load Morning Brief');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  useEffect(() => {
    const anyRunning = briefs.some((b) => b.status === 'running');
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const items = await load();
        if (!items.some((b) => b.status === 'running')) {
          clearInterval(pollRef.current); pollRef.current = null; setGenerating(false);
        }
      }, 3000);
    }
    if (!anyRunning && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, [briefs, load]);

  const generate = async (deliver = false) => {
    setGenerating(true);
    try {
      const res = await api.generateMorningBrief(deliver);
      if (res.status === 'running' && res.id) toast.info('A morning brief is already being generated');
      else toast.success(deliver ? 'Generating & sending your morning brief…' : 'Generating your morning brief…');
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to start generation');
      setGenerating(false);
    }
  };

  const patchCfg = (patch) => setCfg((c) => ({ ...(c || {}), ...patch }));
  const patchChannel = (k, v) => setCfg((c) => ({ ...c, channels: { ...(c?.channels || {}), [k]: v } }));

  const addRule = () => {
    const first = measures[0];
    patchCfg({ threshold_rules: [...(cfg?.threshold_rules || []), {
      model_id: first?.model_id || '', measure: first?.measure || '',
      operator: 'above', limit: 0, label: first?.label || '',
    }] });
  };
  const updateRule = (idx, patch) => {
    const rules = [...(cfg?.threshold_rules || [])];
    rules[idx] = { ...rules[idx], ...patch };
    patchCfg({ threshold_rules: rules });
  };
  const removeRule = (idx) => {
    const rules = [...(cfg?.threshold_rules || [])];
    rules.splice(idx, 1);
    patchCfg({ threshold_rules: rules });
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        ...cfg,
        notify_emails: emailsText.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        notify_slack_channels: slackChannels,
        notify_teams_channels: teamsChannels,
      };
      const res = await api.updateMorningBriefConfig(payload);
      setCfg(res);
      toast.success('Morning Brief settings saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save settings');
    } finally {
      setSavingCfg(false);
    }
  };

  const toggleOnline = async (b) => {
    if (online[b.id]) { setOnline((prev) => { const n = { ...prev }; delete n[b.id]; return n; }); return; }
    setLoadingOnlineId(b.id);
    try {
      const doc = await api.getMorningBrief(b.id);
      setOnline((prev) => ({ ...prev, [b.id]: doc }));
    } catch (e) {
      toast.error(e.message || 'Failed to open brief');
    } finally {
      setLoadingOnlineId(null);
    }
  };

  const copySection = async (bid, section) => {
    try {
      await navigator.clipboard.writeText(section.text || '');
      const k = `${bid}:${section.key}`;
      setCopiedKey(k);
      toast.success(`Copied "${section.title}"`);
      setTimeout(() => setCopiedKey((cur) => (cur === k ? null : cur)), 1600);
    } catch { toast.error('Copy failed — browser blocked clipboard access'); }
  };

  const copyShareText = async (b) => {
    try {
      const res = await api.getMorningBriefShareText(b.id);
      await navigator.clipboard.writeText(res.text || '');
      toast.success('Share text copied to clipboard');
    } catch (e) { toast.error(e.message || 'Share text unavailable'); }
  };

  const remove = async (b) => {
    if (!window.confirm('Delete this morning brief?')) return;
    try {
      await api.deleteMorningBrief(b.id);
      toast.success('Deleted');
      setOnline((prev) => { const n = { ...prev }; delete n[b.id]; return n; });
      load();
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const measuresForModel = (mid) => measures.filter((m) => m.model_id === mid);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="morning-brief-page">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary"><Sunrise className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Morning Brief</h1>
          <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">
            An automated morning scan of your certified measures and pinned KPIs — highlighting
            anomalies, the biggest movers versus the prior period, and any admin thresholds that were
            breached, with a short AI-written summary at the top.
          </p>
        </div>
      </div>

      {/* Generate / preview */}
      <div className="rounded-2xl border border-border/50 bg-card/40 p-5 space-y-3" data-testid="mb-generate-card">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkles className="w-4 h-4 text-primary" /> Preview today's brief
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => generate(false)} disabled={generating} data-testid="mb-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            Generate now
          </Button>
          <Button variant="outline" onClick={() => generate(true)} disabled={generating} data-testid="mb-generate-send-btn" title="Generate the brief and email/Slack/Teams it to the configured recipients now">
            <Send className="w-4 h-4 mr-1.5" /> Generate &amp; send
          </Button>
          <Button variant="outline" onClick={load} data-testid="mb-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <span className="text-xs text-muted-foreground">Runs automatically each morning when enabled below.</span>
        </div>
      </div>

      {/* Settings */}
      {cfg && (
        <div className="rounded-2xl border border-border/50 bg-card/40 p-5 space-y-4" data-testid="mb-settings-card">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Settings2 className="w-4 h-4 text-primary" /> Schedule, delivery & thresholds
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => patchCfg({ enabled: e.target.checked })}
              data-testid="mb-enabled-toggle" className="accent-primary w-4 h-4" />
            Send a brief automatically every morning
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="text-xs text-muted-foreground">Send time (hour, local)</label>
              <select value={cfg.send_hour ?? 7} onChange={(e) => patchCfg({ send_hour: Number(e.target.value) })}
                className={inputCls} data-testid="mb-send-hour">
                {Array.from({ length: 24 }).map((_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Timezone (IANA)</label>
              <Input value={cfg.timezone || 'UTC'} onChange={(e) => patchCfg({ timezone: e.target.value })}
                placeholder="America/New_York" className={inputCls} data-testid="mb-timezone" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Anomaly sensitivity</label>
              <select value={cfg.sensitivity || 'balanced'} onChange={(e) => patchCfg({ sensitivity: e.target.value })}
                className={inputCls} data-testid="mb-sensitivity">
                <option value="conservative">Conservative (fewer alerts)</option>
                <option value="balanced">Balanced</option>
                <option value="aggressive">Aggressive (more alerts)</option>
              </select>
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Delivery channels</div>
            <div className="flex flex-wrap gap-4">
              {['inapp', 'email', 'slack', 'teams'].map((k) => (
                <label key={k} className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer">
                  <input type="checkbox" checked={cfg.channels?.[k] ?? true}
                    onChange={(e) => patchChannel(k, e.target.checked)}
                    data-testid={`mb-channel-${k}`} className="accent-primary w-4 h-4" />
                  {k === 'inapp' ? 'In-app' : k.charAt(0).toUpperCase() + k.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="text-xs text-muted-foreground">Notify emails (comma / newline)</label>
              <Input value={emailsText} onChange={(e) => setEmailsText(e.target.value)}
                placeholder="cfo@acme.com" className={inputCls} data-testid="mb-emails" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Slack channels</label>
              <div className="mt-1"><SlackChannelPicker value={slackChannels} onChange={setSlackChannels} /></div>
              <div className="mt-1.5 flex items-center gap-3">
                <button type="button" onClick={openSlackPreview} disabled={previewLoading}
                  className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="mb-slack-preview-btn">
                  <Hash className="w-3 h-3" /> {previewLoading ? 'Loading preview…' : 'Preview in Slack'}
                </button>
                <button type="button" onClick={sendSlackDm} disabled={dmSending}
                  className="text-[11px] text-cyan-400 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="mb-slack-dm-btn">
                  <Send className="w-3 h-3" /> {dmSending ? 'Sending…' : 'Send to my Slack DM'}
                </button>
                {dmStatus?.dm_ready && (
                  <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="mb-dm-ready-badge">
                    <CheckCircle2 className="w-3 h-3" /> DM ready
                  </span>
                )}
                {dmStatus && !dmStatus.dm_ready && (
                  <span title={dmStatus.reason} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30" data-testid="mb-dm-notready-badge">
                    <AlertTriangle className="w-3 h-3" /> DM not ready
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Teams channels</label>
              <div className="mt-1"><TeamsChannelPicker value={teamsChannels} onChange={setTeamsChannels} /></div>
            </div>
          </div>

          {/* Threshold rules */}
          <div className="space-y-2" data-testid="mb-threshold-rules">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">Threshold rules — flag a measure when it crosses a limit</div>
              <Button size="sm" variant="outline" onClick={addRule} disabled={!measures.length} data-testid="mb-add-rule-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add rule
              </Button>
            </div>
            {!measures.length && (
              <p className="text-xs text-muted-foreground">No monitored measures yet — certify a measure or pin a KPI to add threshold rules.</p>
            )}
            {(cfg.threshold_rules || []).map((r, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border/40" data-testid={`mb-rule-${idx}`}>
                <select value={r.measure ? `${r.model_id}::${r.measure}` : ''}
                  onChange={(e) => {
                    const [mid, meas] = e.target.value.split('::');
                    const m = measures.find((x) => x.model_id === mid && x.measure === meas);
                    updateRule(idx, { model_id: mid, measure: meas, label: m?.label || meas });
                  }}
                  className={`${inputCls} max-w-[240px]`} data-testid={`mb-rule-measure-${idx}`}>
                  {measures.map((m) => (
                    <option key={`${m.model_id}::${m.measure}`} value={`${m.model_id}::${m.measure}`}>
                      {m.label} · {m.model_name}
                    </option>
                  ))}
                </select>
                <select value={r.operator} onChange={(e) => updateRule(idx, { operator: e.target.value })}
                  className={`${inputCls} max-w-[130px]`} data-testid={`mb-rule-op-${idx}`}>
                  <option value="above">is above</option>
                  <option value="below">is below</option>
                  <option value="gte">is ≥</option>
                  <option value="lte">is ≤</option>
                </select>
                <Input type="number" value={r.limit} onChange={(e) => updateRule(idx, { limit: e.target.value })}
                  placeholder="limit" className={`${inputCls} max-w-[120px]`} data-testid={`mb-rule-limit-${idx}`} />
                <Button size="sm" variant="ghost" onClick={() => removeRule(idx)} data-testid={`mb-rule-remove-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          <Button onClick={saveCfg} disabled={savingCfg} data-testid="mb-save-settings-btn">
            {savingCfg ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            Save settings
          </Button>
        </div>
      )}

      {/* History */}
      <div className="space-y-3" data-testid="mb-list">
        {loading ? (
          <div className="text-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : briefs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border/50 p-10 text-center">
            <Sunrise className="w-8 h-8 mx-auto text-muted-foreground/70 mb-2" />
            <p className="text-sm text-muted-foreground">No morning briefs yet. Generate your first one above.</p>
          </div>
        ) : briefs.map((b) => (
          <div key={b.id} className="rounded-2xl border border-border/50 bg-card/40 overflow-hidden" data-testid={`mb-row-${b.id}`}>
            <div className="p-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{b.headline || 'Morning Brief'}</span>
                  <Badge className={`text-[10px] border ${STATUS_CLS[b.status] || ''}`}>{b.status}</Badge>
                  {b.status === 'completed' && b.tone && (
                    <Badge className={`text-[10px] border ${TONE_CLS[b.tone] || ''}`}>{b.tone}</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {b.brief_date || fmtDate(b.created_at)}
                  {b.status === 'completed' && (
                    <> · {b.anomalies_count || 0} anomaly(ies) · {b.breaches_count || 0} breach(es) · {b.metrics_evaluated || 0} metric(s)</>
                  )}
                  {b.status === 'failed' && b.error && <span className="text-destructive"> · {b.error}</span>}
                </div>
              </div>
              {b.status === 'completed' && (
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => toggleOnline(b)} data-testid={`mb-view-${b.id}`}>
                    {loadingOnlineId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : online[b.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    <span className="ml-1">View</span>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => copyShareText(b)} data-testid={`mb-share-${b.id}`}>
                    <Copy className="w-3.5 h-3.5" /><span className="ml-1">Share</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(b)} data-testid={`mb-delete-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              )}
              {b.status !== 'completed' && (
                <Button size="sm" variant="ghost" onClick={() => remove(b)} data-testid={`mb-delete-${b.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              )}
            </div>

            {online[b.id] && (
              <div className="border-t border-border/40 divide-y divide-border/30" data-testid={`mb-online-${b.id}`}>
                {(online[b.id].sections || []).map((sec) => {
                  const k = `${b.id}:${sec.key}`;
                  const isCollapsed = collapsed[k];
                  const Icon = SECTION_ICON[sec.key] || Sparkles;
                  return (
                    <div key={sec.key} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <button
                          className="flex items-center gap-1.5 text-sm font-medium text-foreground"
                          onClick={() => setCollapsed((prev) => ({ ...prev, [k]: !prev[k] }))}
                          data-testid={`mb-section-toggle-${sec.key}`}
                        >
                          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          <Icon className="w-3.5 h-3.5 text-primary" />
                          {sec.title}
                        </button>
                        <Button size="sm" variant="ghost" onClick={() => copySection(b.id, sec)}>
                          {copiedKey === k ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                      {!isCollapsed && (
                        <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed">{sec.text}</pre>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!slackPreview} onOpenChange={(o) => { if (!o) setSlackPreview(null); }}>
        <DialogContent className="max-w-md" data-testid="slack-preview-dialog">
          <DialogHeader><DialogTitle className="text-sm">Slack preview</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs">
            This is exactly how your Morning Brief will appear when posted to {slackPreview?.channels?.length ? `${slackPreview.channels.length} selected channel(s)` : 'Slack'}. Nothing is sent.
          </DialogDescription>
          <div className="rounded-lg border border-border/50 bg-[#1a1d21] p-3 flex gap-2.5" data-testid="slack-preview-body">
            <div className="w-9 h-9 rounded-md bg-[#4a154b] text-white text-sm font-bold flex items-center justify-center shrink-0">IN</div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[13px] font-bold text-white">INZARA</span>
                <span className="text-[10px] px-1 rounded bg-white/10 text-white/60">APP</span>
                <span className="text-[10px] text-white/40">now</span>
              </div>
              <pre className="whitespace-pre-wrap break-words text-[12px] leading-relaxed text-[#e8e8e8] font-sans mt-0.5">{slackPreview?.text}</pre>
            </div>
          </div>
          {slackPreview && !slackPreview.has_brief && (
            <p className="text-[11px] text-amber-400">No completed brief yet — this is a sample layout. Use "Generate now" for a real preview.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
